import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import LegalNav from '../components/LegalNav';
import './LegalPage.css';

function Privacy() {
  useEffect(() => {
    document.title = 'Privacy Policy - My Fair Share UK Tax Calculator';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Privacy policy for My Fair Share. Learn how we protect your data - all tax calculations happen in your browser, and we never store your salary information.');
    }
  }, []);

  return (
    <div className="legal-page">
      <div className="legal-container">
        <Link to="/" className="back-link">← Back to Calculator</Link>
        
        <h1>Privacy Policy</h1>
        <p className="last-updated">Last updated: November 2025</p>

        <section>
          <h2>1. Introduction</h2>
          <p>
            My Fair Share is committed to protecting your privacy. This Privacy Policy explains how we 
            handle information when you use our website and tax calculation tool.
          </p>
          <p>
            This policy applies to all users of My Fair Share and complies with the UK General Data 
            Protection Regulation (UK GDPR) and the Data Protection Act 2018.
          </p>
        </section>

        <section>
          <h2>2. Information We Collect</h2>
          
          <h3>2.1 Salary Data</h3>
          <p>
            When you enter your salary into our calculator, this information is processed 
            <strong> entirely within your browser</strong>. We do not:
          </p>
          <ul>
            <li>Store your salary data on any server</li>
            <li>Transmit your salary data over the internet</li>
            <li>Share your salary data with any third parties</li>
          </ul>
          <p>
            Your salary information exists only in your browser's memory while you use the 
            calculator and is cleared when you close or refresh the page.
          </p>

          <h3>2.2 Cookies</h3>
          <p>
            We use only essential cookies that are strictly necessary for the functioning of 
            our website. We may also use optional cookies for:
          </p>
          <ul>
            <li><strong>Cookie Consent:</strong> To remember your cookie preferences</li>
          </ul>
          <p>
            We do not use tracking cookies or third-party advertising cookies.
          </p>

          <h3>2.3 Technical Information</h3>
          <p>
            Our hosting provider may automatically collect limited technical information when 
            you visit our website, including:
          </p>
          <ul>
            <li>IP address (anonymised where possible)</li>
            <li>Browser type and version</li>
            <li>Operating system</li>
            <li>Date and time of visit</li>
            <li>Pages visited</li>
          </ul>
          <p>
            This information is used solely for security purposes and to ensure our website 
            functions correctly.
          </p>
        </section>

        <section>
          <h2>3. How We Use Your Information</h2>
          <p>
            Any technical information collected is used only to:
          </p>
          <ul>
            <li>Operate and maintain our website</li>
            <li>Protect against security threats</li>
            <li>Improve our services</li>
            <li>Comply with legal obligations</li>
          </ul>
        </section>

        <section>
          <h2>4. Legal Basis for Processing</h2>
          <p>
            Under UK GDPR, we process personal data on the following lawful bases:
          </p>
          <ul>
            <li>
              <strong>Legitimate interests:</strong> For basic analytics and security purposes
            </li>
            <li>
              <strong>Consent:</strong> For any optional cookies (you can withdraw consent at any time)
            </li>
            <li>
              <strong>Legal obligation:</strong> Where we are required by law to process data
            </li>
          </ul>
        </section>

        <section>
          <h2>5. Data Sharing</h2>
          <p>
            We do not sell, trade, or rent your personal information to third parties.
          </p>
          <p>
            We may share limited technical data with:
          </p>
          <ul>
            <li>Our hosting provider (AWS) for infrastructure purposes</li>
            <li>Law enforcement if required by law</li>
          </ul>
        </section>

        <section>
          <h2>6. Data Retention</h2>
          <p>
            Salary data you enter is not retained - it exists only temporarily in your browser.
          </p>
          <p>
            Server logs containing technical information may be retained for up to 90 days 
            for security purposes.
          </p>
          <p>
            Cookie consent preferences are stored for up to 365 days.
          </p>
        </section>

        <section>
          <h2>7. Your Rights</h2>
          <p>
            Under UK GDPR, you have the following rights:
          </p>
          <ul>
            <li><strong>Right of access:</strong> Request copies of your personal data</li>
            <li><strong>Right to rectification:</strong> Request correction of inaccurate data</li>
            <li><strong>Right to erasure:</strong> Request deletion of your data</li>
            <li><strong>Right to restrict processing:</strong> Request limitation of data processing</li>
            <li><strong>Right to data portability:</strong> Request transfer of your data</li>
            <li><strong>Right to object:</strong> Object to processing of your data</li>
            <li><strong>Rights related to automated decision-making:</strong> Not applicable as we don't use automated decision-making</li>
          </ul>
          <p>
            To exercise any of these rights, please <Link to="/contact">contact us</Link>.
          </p>
        </section>

        <section>
          <h2>8. Children's Privacy</h2>
          <p>
            Our website is not directed at children under 16. We do not knowingly collect 
            personal information from children.
          </p>
        </section>

        <section>
          <h2>9. International Transfers</h2>
          <p>
            Your data may be processed on servers located outside the UK. Where this occurs, 
            we ensure appropriate safeguards are in place in accordance with UK GDPR.
          </p>
        </section>

        <section>
          <h2>10. Security</h2>
          <p>
            We implement appropriate technical and organisational measures to protect your 
            data, including:
          </p>
          <ul>
            <li>HTTPS encryption for all data in transit</li>
            <li>Client-side processing for sensitive calculations</li>
            <li>Regular security updates</li>
          </ul>
        </section>

        <section>
          <h2>11. Changes to This Policy</h2>
          <p>
            We may update this Privacy Policy from time to time. Changes will be posted on 
            this page with an updated revision date. We encourage you to review this policy 
            periodically.
          </p>
        </section>

        <section>
          <h2>12. Contact Us</h2>
          <p>
            If you have any questions about this Privacy Policy or wish to exercise your 
            data protection rights, please <Link to="/contact">contact us</Link>.
          </p>
          <p>
            You also have the right to lodge a complaint with the Information Commissioner's 
            Office (ICO) at <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer">ico.org.uk</a>.
          </p>
        </section>

        <LegalNav excludeLinks={['/privacy']} />
      </div>
    </div>
  );
}

export default Privacy;
